import { InfoOutlined } from '@mui/icons-material'
import { Box, Tooltip, useTheme } from '@mui/material'
import { t } from 'i18next'
import { ReactElement, ReactNode, useState, useEffect } from 'react'
import { isCypress } from 'utils/cypress'
import { isTestEnv } from 'utils/env'
import { isTrulyEmpty } from 'utils/object'

interface GroupcalTooltipProps {
  children: ReactElement | ReactNode
  title?: ReactNode
  delay?: number
  color?: string
  enterDelay?: number
  placement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top'
  backgroundColor?: string
  textColor?: string
  textSize?: string
  enterNextDelay?: number
  hidden?: boolean
  tooltipStyles?: React.CSSProperties
  showArrow?: boolean
  byPassCypressTest?: boolean
  image?: string
  link?: string
  linkText?: string
}

export default function GroupcalTooltip(props: GroupcalTooltipProps) {
  const theme = useTheme()
  const [imageLoaded, setImageLoaded] = useState(!props.image)

  const tooltipBackground = props.backgroundColor
  const tooltipTextColor = props.textColor
  const linkColor = (theme.palette.button.text.link as string) || '#1976d2'

  // Pre-load image on mount
  useEffect(() => {
    if (props.image) {
      const img = new Image()
      img.onload = () => setImageLoaded(true)
      img.onerror = () => setImageLoaded(true) // Proceed even if image fails to load
      img.src = props.image
    }
  }, [props.image])

  if (
    props.hidden ||
    (isCypress() && !props.byPassCypressTest) ||
    props.title === undefined ||
    (typeof props.title === 'string' && isTrulyEmpty(props.title))
  ) {
    return <div>{props.children}</div>
  }

  // Build tooltip content with optional image and link
  const tooltipContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%'
      }}
    >
      {/* Main content/title */}
      <Box sx={{ mb: props.image ? '4px' : '0px' }}>{props.title}</Box>

      {/* Image - pre-loaded so size is known on hover */}
      {props.image && imageLoaded && (
        <img
          src={props.image}
          alt="tooltip-image"
          style={{
            width: '100%',
            height: 'auto',
            borderRadius: '8px',
            display: 'block',
            marginBottom: props.link ? '8px' : '0px'
          }}
        />
      )}

      {/* Read more link - "Read more - " prefix with bold link text */}
      {props.link && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{ fontWeight: 'bold' }}>{`${t('readMore')} - `}</span>
          <Box
            component="a"
            href={props.link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: linkColor,
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.85
              }
            }}
          >
            {props.linkText}
          </Box>
        </Box>
      )}
    </Box>
  )

  return (
    <Tooltip
      leaveDelay={200}
      enterDelay={props.enterDelay ?? 500}
      enterNextDelay={props.enterNextDelay ?? 600}
      placement={props.placement ?? 'bottom'}
      arrow={props.showArrow}
      title={tooltipContent}
      slotProps={{
        tooltip: {
          sx: {
            display: 'block',
            padding: '16px',
            fontSize: props.textSize ?? '12px',
            lineHeight: '22px',
            letterSpacing: '0.5px',
            margin: '4px !important',
            borderRadius: '8px',
            maxWidth: '500px',
            backgroundColor: tooltipBackground,
            color: tooltipTextColor,
            border: '1px solid var(--color-border-overlay)',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0px 8px 24px rgba(0, 0, 0, 0.45)'
                : '0px 8px 24px rgba(0, 0, 0, 0.15)',
            '& .MuiTooltip-arrow::before': {
              backgroundColor: `${tooltipBackground} !important`
            },
            ...props.tooltipStyles
          }
        },
        popper: {
          sx: {
            zIndex: 999998
          }
        }
      }}
    >
      {/* This div now controls the visibility of the tooltip */}
      <Box sx={{ display: 'flex', height: 'fit-content' }}>
        {props.children}
      </Box>
    </Tooltip>
  )
}
